/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// Wisps: every so often, a glyph in the background field comes alive. It keeps its glyph and
// color, lifts out of its cell, and swims around the field in big circles until a click or a
// wave pops it. This file moves the wisps; field.ts draws them. Lengths are in viewport CSS pixels,
// and times in seconds.
//
// The wake: each point along a wisp's path grows a ring with the same cross-section as a wave
// (`waveShape` in waves/waves.ts). The rings grow slower than the wisp swims, so they overlap
// into a V behind it, like a boat's wake:
//
//        ((((((((((  o  <- wisp
//
// The shaders (field-shaders.ts) read the array sizes and the wake constants from here.

import { gustOffset } from "@bfly/engine";
import { ageAt, channelOf, type WaveAges } from "../waves/wave-ages.js";
import { smoothstep } from "@bfly/engine/math";
import { WAVE_SHAPE, type WaveRing } from "../waves/waves.js";

// The shaders' array size: at most this many wisps at once.
export const WISP_SLOTS = 5;
// Below this viewport width (in CSS pixels), the strip of field beside the content is too thin to
// swim in, so there are no wisps.
export const WISP_MIN_VIEWPORT = 900;
export const WAKE_SAMPLES = 24;
// At most `WISP_MAX` wisps at a time, and a new one about every `WISP_EVERY` seconds.
const WISP_MAX = WISP_SLOTS;
const WISP_EVERY = 2.5;

// A wisp lifts out of its cell over `WISP_LIFT` seconds, growing to `WISP_SCALE`, then speeds up
// to `WISP_SPEED` over `WISP_LAUNCH` seconds.
const WISP_SCALE = 1.3;
const WISP_LIFT = 0.5;
const WISP_LAUNCH = 0.8;
const WISP_SPEED = 90;
// It swims in turns with a radius of `WISP_RADIUS_MIN` to `WISP_RADIUS_MAX`, picked on a log scale
// so tight loops come up often. Every `WISP_TURN_MIN` to `WISP_TURN_MAX` seconds it picks a new
// radius and eases into it (so it spirals), plus a new pace of `WISP_PACE_MIN` to `WISP_PACE_MAX`
// times `WISP_SPEED`. Each new turn has a `WISP_FLIP` chance of switching direction.
const WISP_RADIUS_MIN = 35;
const WISP_RADIUS_MAX = 220;
const WISP_TURN_MIN = 0.8;
const WISP_TURN_MAX = 3.5;
const WISP_FLIP = 0.5;
const WISP_PACE_MIN = 0.7;
const WISP_PACE_MAX = 1.5;
// How fast the radius and pace ease toward their targets, per second.
const WISP_EASE = 1.5;
// Sometimes (`WISP_LOOP_ODDS`) it does loops instead: `WISP_LOOPS_MIN` to `WISP_LOOPS_MAX` full
// turns on a small circle.
const WISP_LOOP_ODDS = 0.2;
const WISP_LOOPS_MIN = 0.5;
const WISP_LOOPS_MAX = 3.5;
const WISP_LOOP_RADIUS_MIN = 30;
const WISP_LOOP_RADIUS_MAX = 50;
// It looks `WISP_LOOKAHEAD` pixels ahead and makes a tight turn (`WISP_TIGHT` radius) away from the
// dithered band around the content (closer than `WISP_BAND_CLEAR`) or the edge of the screen
// (closer than `WISP_MARGIN`). The turn itself can carry it `WISP_TIGHT` closer before it heads
// away, so the clearance is that plus half a glyph.
const WISP_LOOKAHEAD = 80;
const WISP_TIGHT = 45;
const WISP_BAND_CLEAR = WISP_TIGHT + 8;
const WISP_MARGIN = 24;
// Other wisps count as obstacles too: it turns away from one within `WISP_AVOID` ahead.
const WISP_AVOID = 90;
// New wisps start at least `WISP_START_CLEAR` outside the band (past where they'd turn away)
// and `WISP_START_MARGIN` from the edge of the screen.
const WISP_START_CLEAR = 60;
const WISP_START_MARGIN = 48;
// Wisps only start from the field's two lightest glyphs (`GLYPHS` in atlas.ts). The bigger
// ones looked too busy.
const WISP_GLYPHS: readonly string[] = ["∘", "·"];
// The wake drops a point every `WAKE_STEP` seconds. Each one grows a ring at `WAKE_SPEED`, about
// `WAKE_THICK` pixels thick (plus `WAKE_GROW` of its radius). The ring starts at `WAKE_HEIGHT` and
// fades out over its life.
const WAKE_STEP = 0.08;
const WAKE_LIFE = WAKE_SAMPLES * WAKE_STEP;
const WAKE_SPEED = 45;
export const WAKE_THICK = 6;
export const WAKE_GROW = 0.1;
const WAKE_HEIGHT = 0.2;
// A wake scales its glyphs half as much as a wave of the same height would. It has so many
// rings that at full strength it swelled too much. The fade is the same as a wave's.
export const WAKE_SCALE_SHARE = 0.5;
// The trail: every cell a wisp leaves shows the wisp's glyph for a moment. The wisp itself
// is fully inverted; a trail dot starts at `TRAIL_START` and fades back to the cell's own color
// over `TRAIL_LIFE`, which is about 4 dots at cruising pace. Then the cell goes back to its own
// glyph. The shaders keep `TRAIL_DOTS` per wisp, enough for the fastest pace.
export const TRAIL_DOTS = 8;
const TRAIL_START = 0.8;
const TRAIL_LIFE = 0.4;
// Pops: a click near a wisp pops it, and so does a wave that reaches it. The wisp vanishes
// over `POP_FADE`, and `POP_RAYS` rays of dots burst out from where it was, like a little
// explosion. The rays reach `POP_REACH` cell heights at most, slow down (time constant
// `POP_DECAY`), and are gone by `POP_HEAD_END`. Every cell a ray passes shows the wisp's glyph
// like a trail dot, fading from `POP_TRAIL_START` over `POP_TRAIL_LIFE` with a hard ease-out
// (cubic), so it drops fast and lingers faint.
//
// The click target is generous: `POP_HIT` pixels around the wisp, or around where it was a frame or
// two ago (0.08s to 0.16s), so a click lands where your eye saw it. The wisp alone was nearly
// impossible to click.
//
// A popped wisp keeps its slot until the pop is over. The shader draws the pops
// (`popAt` in field-shaders.ts), which is why these are exported.
export const POP_RAYS = 12;
export const POP_REACH = 6;
export const POP_DECAY = 0.11;
export const POP_HEAD_END = 0.45;
export const POP_TRAIL_START = TRAIL_START;
export const POP_TRAIL_LIFE = 0.7;
const POP_HIT = 28;
const POP_FADE = 0.06;
// How long until a pop has nothing left to show.
const POP_LIFE = POP_HEAD_END + POP_TRAIL_LIFE;

// The glyphs around a wisp shrink and fade like it's pushing them aside, out to `WISP_HULL` of
// its size (about 1.2 cell heights at `WISP_SCALE`), so it swims in a little clearing.
export const WISP_HULL = 0.93;

// A field cell: its column and row, and its center in pixels.
interface WispCell {
  x: number;
  y: number;
  cx: number;
  cy: number;
}

// What the wisps need to know about the field, every frame (field.ts).
export interface WispEnvironment {
  width: number;
  height: number;
  // The carve's center.
  carveCenterX: number;
  // How far a point is outside the carve, in band widths (same math as the shader).
  carveEdge(x: number, y: number): number;
  // The band's width in CSS pixels.
  band: number;
  // The field cell at a point.
  cellAt(x: number, y: number): WispCell;
  // The glyph a cell shows right now.
  glyphAt(cell: WispCell): string;
  time: number;
  // How far the scroll has shifted the pattern, in rows (parallax.ts).
  patternShift: number;
}

// Writes four values at `offset`. Avoids making a little array for every write in `pack()`.
function put4(
  target: Float32Array,
  offset: number,
  a: number,
  b: number,
  c: number,
  d: number,
): void {
  target[offset] = a;
  target[offset + 1] = b;
  target[offset + 2] = c;
  target[offset + 3] = d;
}

// The shader uniforms that `pack()` fills:
// - per wisp: position, scale, and opacity; the pattern cell and time its glyph came from;
//   its wake's bounding box; and its trail's bounding box, in cells
// - per wake point: x, y, radius, and height
// - per trail dot: cell x, y, and how inverted it is
interface PackedWisps {
  wisp: Float32Array;
  source: Float32Array;
  time: Float32Array;
  wake: Float32Array;
  box: Float32Array;
  trail: Float32Array;
  trailBox: Float32Array;
  pop: Float32Array;
  popSpin: Float32Array;
}

export interface Wisps {
  // Moves the wisps and maybe starts a new one. Returns whether anything is still moving.
  update(now: number, dt: number, env: WispEnvironment): boolean;
  // The portrait's live waves (in viewport CSS pixels; empty once they're done) and the field's
  // ages (wave-ages.ts), or null without them. A wave pops every wisp it reaches: by the
  // ages if there are any, otherwise by the gust's front. No new wisps start during a wave,
  // or right after one.
  setWaves(rings: readonly WaveRing[], now: number, ages: WaveAges | null): void;
  // Whether a click at this point (in viewport CSS pixels) would pop a wisp.
  hit(x: number, y: number): boolean;
  // Pops the wisp at this point, if there is one. Returns true if it did.
  pop(x: number, y: number, now: number): boolean;
  clear(): void;
  // The shader uniforms for this moment, in device pixels.
  pack(now: number, dpr: number): PackedWisps;
}

interface Wisp {
  x: number;
  y: number;
  heading: number;
  omega: number;
  turnSign: number;
  radius: number;
  radiusTarget: number;
  pace: number;
  paceTarget: number;
  // Radians of loop still to turn, or 0 when cruising.
  loopLeft: number;
  nextTurnAt: number;
  steerUntil: number;
  bornAt: number;
  // When and where it popped, and the angle of the pop's first ray.
  popped: { at: number; x: number; y: number; spin: number } | null;
  visible: number;
  quadrant: number;
  // Enough for the shader to draw the glyph the wisp started as. The cell is in pattern
  // rows, so scrolling later doesn't change it.
  source: { x: number; y: number; time: number };
  samples: { x: number; y: number; t: number; strength: number }[];
  lastSampleAt: number;
  // The cell the wisp is over, and the cells it's left behind, oldest first.
  cell: { x: number; y: number };
  trail: { x: number; y: number; t: number }[];
}

// How far a point is from the segment between two others.
function segmentDistance(
  x: number,
  y: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const length2 = dx * dx + dy * dy;
  const t = length2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / length2)) : 0;
  return Math.hypot(x - ax - t * dx, y - ay - t * dy);
}

export function createWisps(random: () => number = Math.random): Wisps {
  const wispBetween = (min: number, max: number): number => min + random() * (max - min);
  const wispLogBetween = (min: number, max: number): number => min * (max / min) ** random();

  const slots: (Wisp | null)[] = Array.from({ length: WISP_SLOTS }, () => null);
  let nextSpawnAt: number | null = null;
  let rippleLive = false;
  const packed: PackedWisps = {
    wisp: new Float32Array(WISP_SLOTS * 4),
    source: new Float32Array(WISP_SLOTS * 2),
    time: new Float32Array(WISP_SLOTS),
    wake: new Float32Array(WISP_SLOTS * WAKE_SAMPLES * 4),
    box: new Float32Array(WISP_SLOTS * 4),
    trail: new Float32Array(WISP_SLOTS * TRAIL_DOTS * 3),
    trailBox: new Float32Array(WISP_SLOTS * 4),
    pop: new Float32Array(WISP_SLOTS * 4),
    popSpin: new Float32Array(WISP_SLOTS),
  };

  // How far a point is outside the band around the carve, in CSS pixels (negative inside it).
  const outsideBand = (x: number, y: number, env: WispEnvironment): number =>
    (env.carveEdge(x, y) - 1) * env.band;

  // How much room there is at a point. Negative means too close to the band or the edge.
  function clearance(x: number, y: number, env: WispEnvironment): number {
    const toEdge = Math.min(x, y, env.width - x, env.height - y) - WISP_MARGIN;
    return Math.min(outsideBand(x, y, env) - WISP_BAND_CLEAR, toEdge) / 100;
  }

  // Starts a new stretch of swimming: a new radius and pace to ease toward, maybe in the
  // other direction. Now and then it's a loop instead, which `move()` ends after its turns.
  function newCruise(wisp: Wisp, now: number): void {
    if (random() < WISP_FLIP) wisp.turnSign = -wisp.turnSign;
    if (random() < WISP_LOOP_ODDS) {
      wisp.radius = wispBetween(WISP_LOOP_RADIUS_MIN, WISP_LOOP_RADIUS_MAX);
      wisp.radiusTarget = wisp.radius;
      wisp.loopLeft = wispBetween(WISP_LOOPS_MIN, WISP_LOOPS_MAX) * 2 * Math.PI;
      wisp.nextTurnAt = Infinity;
      return;
    }
    wisp.radiusTarget = wispLogBetween(WISP_RADIUS_MIN, WISP_RADIUS_MAX);
    wisp.paceTarget = wispBetween(WISP_PACE_MIN, WISP_PACE_MAX);
    wisp.nextTurnAt = now + wispBetween(WISP_TURN_MIN, WISP_TURN_MAX);
  }

  // How much room there is from the nearest other wisp. Negative means closer than
  // `WISP_AVOID`.
  function crowding(x: number, y: number, self: Wisp): number {
    let nearest = Infinity;
    for (const other of slots) {
      if (other && other !== self && other.popped === null) {
        nearest = Math.min(nearest, Math.hypot(other.x - x, other.y - y));
      }
    }
    return (nearest - WISP_AVOID) / 100;
  }

  function steer(wisp: Wisp, now: number, dt: number, env: WispEnvironment): void {
    const ease = 1 - Math.exp(-WISP_EASE * dt);
    // The radius eases in log space, so a tight loop opens up as smoothly as a wide turn
    // closes in.
    wisp.radius *= (wisp.radiusTarget / wisp.radius) ** ease;
    wisp.pace += (wisp.paceTarget - wisp.pace) * ease;
    const tight = (WISP_SPEED * wisp.pace) / WISP_TIGHT;
    const look = (angle: number): number => {
      const x = wisp.x + Math.cos(angle) * WISP_LOOKAHEAD;
      const y = wisp.y + Math.sin(angle) * WISP_LOOKAHEAD;
      return Math.min(clearance(x, y, env), crowding(x, y, wisp));
    };
    // Stuck too close to something (a scroll or resize can do that): head for open field.
    if (clearance(wisp.x, wisp.y, env) < 0) {
      const target =
        outsideBand(wisp.x, wisp.y, env) < WISP_BAND_CLEAR
          ? wisp.x < env.carveCenterX
            ? Math.PI
            : 0
          : Math.atan2(env.height / 2 - wisp.y, env.width / 2 - wisp.x);
      const turn = Math.atan2(Math.sin(target - wisp.heading), Math.cos(target - wisp.heading));
      wisp.omega = (turn >= 0 ? 1 : -1) * tight;
      return;
    }
    // Something ahead: turn toward whichever side is more open, and stick with it for the
    // whole turn. Then the cruise widens out of the turn, same direction, back into the field.
    if (look(wisp.heading) < 0) {
      if (now >= wisp.steerUntil) {
        wisp.turnSign = look(wisp.heading + 0.7) >= look(wisp.heading - 0.7) ? 1 : -1;
        wisp.radius = WISP_TIGHT;
        // A loop that runs into something ends here. The next stretch starts after the turn.
        wisp.loopLeft = 0;
        wisp.nextTurnAt = now;
      }
      wisp.omega = wisp.turnSign * tight;
      wisp.steerUntil = now + 0.3;
      return;
    }
    if (now < wisp.steerUntil) return;
    if (now >= wisp.nextTurnAt) newCruise(wisp, now);
    wisp.omega = (wisp.turnSign * WISP_SPEED * wisp.pace) / wisp.radius;
  }

  // A new wisp on a field cell, or null if there's no good spot. It starts in whichever
  // quadrant has the fewest wisps, so up to four never have to share; ties are random.
  function spawn(now: number, env: WispEnvironment): Wisp | null {
    const starts = [0, 0, 0, 0];
    for (const wisp of slots) if (wisp) starts[wisp.quadrant] = (starts[wisp.quadrant] ?? 0) + 1;
    const quadrants = [0, 1, 2, 3];
    for (let i = quadrants.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      const swap = quadrants[i] ?? i;
      quadrants[i] = quadrants[j] ?? j;
      quadrants[j] = swap;
    }
    quadrants.sort((a, b) => (starts[a] ?? 0) - (starts[b] ?? 0));
    for (const quadrant of quadrants) {
      for (let attempt = 0; attempt < 40; attempt++) {
        const x = (((quadrant % 2) + random()) * env.width) / 2;
        const y = ((Math.floor(quadrant / 2) + random()) * env.height) / 2;
        if (Math.min(x, y, env.width - x, env.height - y) < WISP_START_MARGIN) continue;
        if (outsideBand(x, y, env) < WISP_START_CLEAR) continue;
        const cell = env.cellAt(x, y);
        if (!WISP_GLYPHS.includes(env.glyphAt(cell))) continue;
        const wisp: Wisp = {
          x: cell.cx,
          y: cell.cy,
          heading: random() * 2 * Math.PI,
          omega: 0,
          turnSign: random() < 0.5 ? 1 : -1,
          radius: wispLogBetween(WISP_RADIUS_MIN, WISP_RADIUS_MAX),
          radiusTarget: 0,
          pace: 1,
          paceTarget: 1,
          loopLeft: 0,
          nextTurnAt: 0,
          steerUntil: 0,
          bornAt: now,
          popped: null,
          visible: 1,
          quadrant,
          source: { x: cell.x, y: cell.y + env.patternShift, time: env.time },
          samples: [],
          lastSampleAt: now,
          cell: { x: cell.x, y: cell.y },
          trail: [],
        };
        newCruise(wisp, now);
        return wisp;
      }
    }
    return null;
  }

  // A popped wisp stops where it is and fades over `POP_FADE` while the pop starts. Its trail
  // keeps fading on its own schedule.
  function popWisp(wisp: Wisp, now: number): void {
    const spin = (random() * 2 * Math.PI) / POP_RAYS;
    wisp.popped = { at: now, x: wisp.x, y: wisp.y, spin };
  }
  // The wisp a click here would pop: the nearest visible one in reach, measured to its path
  // over the last couple of wake points, not just where it is now.
  function wispAt(x: number, y: number): number {
    let found = -1;
    let nearest = POP_HIT;
    for (let i = 0; i < slots.length; i++) {
      const wisp = slots[i];
      if (!wisp || wisp.popped !== null || wisp.visible < 0.5) continue;
      const from = wisp.samples.at(-2) ?? wisp.samples.at(-1) ?? wisp;
      const distance = segmentDistance(x, y, from.x, from.y, wisp.x, wisp.y);
      if (distance <= nearest) {
        nearest = distance;
        found = i;
      }
    }
    return found;
  }

  function move(wisp: Wisp, now: number, dt: number, env: WispEnvironment): void {
    // Wisps can swim under the content; they fade out there.
    wisp.visible = smoothstep(0, 1, (env.carveEdge(wisp.x, wisp.y) - 0.1) / 0.5);
    if (wisp.popped) return;
    const launch = smoothstep(0, 1, (now - wisp.bornAt - WISP_LIFT) / WISP_LAUNCH);
    if (launch <= 0) return;
    steer(wisp, now, dt, env);
    // Turning scales with the launch too, so a wisp that's still speeding up keeps its circle.
    const speed = WISP_SPEED * wisp.pace * launch;
    const turned = wisp.omega * launch * dt;
    wisp.heading += turned;
    if (wisp.loopLeft > 0) {
      wisp.loopLeft -= Math.abs(turned);
      if (wisp.loopLeft <= 0) newCruise(wisp, now);
    }
    wisp.x += Math.cos(wisp.heading) * speed * dt;
    wisp.y += Math.sin(wisp.heading) * speed * dt;
    const cell = env.cellAt(wisp.x, wisp.y);
    if (cell.x !== wisp.cell.x || cell.y !== wisp.cell.y) {
      wisp.trail.push({ x: wisp.cell.x, y: wisp.cell.y, t: now });
      if (wisp.trail.length > TRAIL_DOTS) wisp.trail.shift();
      wisp.cell = { x: cell.x, y: cell.y };
    }
    if (now - wisp.lastSampleAt >= WAKE_STEP) {
      wisp.samples.push({ x: wisp.x, y: wisp.y, t: now, strength: launch });
      if (wisp.samples.length > WAKE_SAMPLES) wisp.samples.shift();
      wisp.lastSampleAt = now;
    }
  }

  // Packs the trail dots (newest last) and the box around their cells.
  function packTrail(wisp: Wisp, slot: number, now: number): void {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let k = 0; k < wisp.trail.length; k++) {
      const dot = wisp.trail[k]!;
      const level = TRAIL_START * Math.max(0, 1 - (now - dot.t) / TRAIL_LIFE);
      const at = (slot * TRAIL_DOTS + k) * 3;
      packed.trail[at] = dot.x;
      packed.trail[at + 1] = dot.y;
      packed.trail[at + 2] = level;
      minX = Math.min(minX, dot.x);
      minY = Math.min(minY, dot.y);
      maxX = Math.max(maxX, dot.x);
      maxY = Math.max(maxY, dot.y);
    }
    if (wisp.trail.length) put4(packed.trailBox, slot * 4, minX, minY, maxX, maxY);
  }

  return {
    update(now, dt, env) {
      const every = (): number => now + WISP_EVERY * wispBetween(0.5, 1.5);
      nextSpawnAt ??= every();
      if (now >= nextSpawnAt) {
        nextSpawnAt = every();
        const free = slots.indexOf(null);
        const live = slots.filter((wisp) => wisp !== null).length;
        if (!rippleLive && free >= 0 && live < WISP_MAX) slots[free] = spawn(now, env);
      }
      for (let i = 0; i < slots.length; i++) {
        const wisp = slots[i];
        if (!wisp) continue;
        move(wisp, now, dt, env);
        while (wisp.samples.length && now - (wisp.samples[0]?.t ?? now) > WAKE_LIFE) {
          wisp.samples.shift();
        }
        while (wisp.trail.length && now - (wisp.trail[0]?.t ?? now) > TRAIL_LIFE) {
          wisp.trail.shift();
        }
        // A popped wisp keeps its slot until its pop, wake, and trail are all gone, because
        // the pop's dots still draw its glyph.
        const gone = wisp.popped !== null && now - wisp.popped.at > POP_LIFE;
        const settled = wisp.samples.length === 0 && wisp.trail.length === 0;
        if (gone && settled) slots[i] = null;
      }
      return slots.some((wisp) => wisp !== null);
    },

    setWaves(rings, now, ages) {
      if (rings.length === 0) {
        if (rippleLive) nextSpawnAt = now + WISP_EVERY * wispBetween(0.5, 1.5);
        rippleLive = false;
        return;
      }
      rippleLive = true;
      for (const ring of rings) {
        const channel = ages ? channelOf(ages, ring.id) : -1;
        for (const wisp of slots) {
          if (!wisp || wisp.popped !== null) continue;
          const reached = ages
            ? ageAt(ages, channel, wisp.x, wisp.y) !== null
            : gustOffset(ring, wisp.x, wisp.y) <= 0;
          if (reached) popWisp(wisp, now);
        }
      }
    },

    hit(x, y) {
      return wispAt(x, y) >= 0;
    },

    pop(x, y, now) {
      const wisp = slots[wispAt(x, y)];
      if (!wisp) return false;
      popWisp(wisp, now);
      return true;
    },

    clear() {
      slots.fill(null);
      nextSpawnAt = null;
    },

    pack(now, dpr) {
      packed.wake.fill(0);
      packed.trail.fill(0);
      for (let i = 0; i < WISP_SLOTS; i++) {
        const wisp = slots[i];
        put4(packed.box, i * 4, 1, 1, 0, 0);
        put4(packed.trailBox, i * 4, 1, 1, 0, 0);
        // A pop: its center in device pixels, its age in seconds, and 1 (or -1 for no pop).
        const popped = wisp?.popped ?? null;
        if (popped) put4(packed.pop, i * 4, popped.x * dpr, popped.y * dpr, now - popped.at, 1);
        else put4(packed.pop, i * 4, 0, 0, 0, -1);
        packed.popSpin[i] = popped?.spin ?? 0;
        if (!wisp) {
          put4(packed.wisp, i * 4, 0, 0, 1, 0);
          continue;
        }
        packTrail(wisp, i, now);
        const grow = smoothstep(0, 1, (now - wisp.bornAt) / WISP_LIFT);
        const fade = popped ? smoothstep(0, 1, (now - popped.at) / POP_FADE) : 0;
        const scale = Math.max(0.01, (1 + (WISP_SCALE - 1) * grow) * (1 - fade));
        put4(packed.wisp, i * 4, wisp.x * dpr, wisp.y * dpr, scale, (1 - fade) * wisp.visible);
        const { source } = wisp;
        packed.source[i * 2] = source.x;
        packed.source[i * 2 + 1] = source.y;
        packed.time[i] = source.time;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (let k = 0; k < wisp.samples.length; k++) {
          const sample = wisp.samples[k]!;
          const age = Math.max(0, now - sample.t);
          const radius = WAKE_SPEED * age;
          const life = Math.max(0, 1 - age / WAKE_LIFE);
          const height = WAKE_HEIGHT * sample.strength * life * life;
          const reach = radius + WAVE_SHAPE.lead * (WAKE_THICK + WAKE_GROW * radius);
          minX = Math.min(minX, sample.x - reach);
          minY = Math.min(minY, sample.y - reach);
          maxX = Math.max(maxX, sample.x + reach);
          maxY = Math.max(maxY, sample.y + reach);
          put4(
            packed.wake,
            (i * WAKE_SAMPLES + k) * 4,
            sample.x * dpr,
            sample.y * dpr,
            radius * dpr,
            height,
          );
        }
        if (wisp.samples.length)
          put4(packed.box, i * 4, minX * dpr, minY * dpr, maxX * dpr, maxY * dpr);
      }
      return packed;
    },
  };
}
